/* eslint-disable jsx-a11y/anchor-is-valid */
import { createRef, useCallback, useContext, useEffect, useState } from "react";
import {
  FilesStructure,
  Dataset,
  FileStructureVersion,
  FileStructureItem,
  datasetsConstants,
  DatasetForUploadWriteModel,
} from "../../../api/Datasets";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { LoadingWrapper } from "../../../common/LoadingWrapper";
import { Container } from "../../../common/panels/Container/Container";
import { useFileStructureVersions } from "../../common/Tree/Folder/helpers/FileHelpers";
import { DatasetFilesControls } from "../../common/Tree/Folder/helpers/FilePreview";
import { FileTree } from "../../common/Tree/Tree";
import { DatasetContext, FullScreenOpts } from "../../Dataset";
import styles from "./Files.module.css";
import { Skeleton } from "../../../common/loaders/Skeleton/Skeleton";
import { GenericVirtualizedSelectFormField } from "../../../common/formfields/GenericVirtualizedSelectFormField/GenericVirtualizedSelectFormField";
import { useForm } from "react-hook-form";
import { DownloadDatasetVersion } from "../../common/DownloadDataset/DownloadDataset";
import { SessionContext } from "../../../common/contexts/SessionContext";
import { DateTimeRenderer } from "../../../common/datetime/DateTimeFormatter";
import { useHistory } from "react-router-dom";
import { getDetailLink } from "../../../main/Routing";
import { Alert } from "../../../common/overlays/Alert/Alert";
import { Button } from "../../../common/buttons/Button/Button";
import { useFileHandlingUtils } from "../../ManualUpload/FileHandlingUtils";
import { defaultDatasetParsersBlackList } from "../../ManualUpload/ManualUploadDropArea";
import { showtoast } from "../../../common/overlays/Toasts/showtoast";
import { useQueryClient } from "@tanstack/react-query";
import { FloatingDiv } from "../../../common/buttons/FloatingDiv/FloatingDiv";
import { useDatasetUploadUtils, useFileUploadUtils } from "../../ManualUpload/FileUploadUtils";
import { useMatch } from "../../ManualUpload/FileFormatAutoDetection";

export interface TreeElementBase {
  id: string;
  name: string;
  parentId: string | undefined;
  hasChildren: [];
  size: number;
  mtime: number;
  path: string;
  hash?: string;
  contents: [];
  uniqueId: string;
  fileVersion?: number;
}
export type TreeElement<T> = T & TreeElementBase;
export type TreeElements<T> = TreeElement<T>[];

export type FilesTreeElement = FileStructureItem & TreeElementBase;

interface FileStructureVersionEntry {
  datasetVersion: number;
  structure: FileStructureVersion;
}

export const getTreeData = <T extends TreeElementBase>(treeData: TreeElements<T>): TreeElements<T> => {
  if (!treeData) return [];
  if (Array.isArray(treeData) && treeData.length === 0) return [];

  return treeData.map((item: T) => ({
    ...item,
    hasChildren: treeData.filter((i) => i.parentId === item.id).map((d) => d.id),
  })) as TreeElements<T>;
};

/**
 * Recursively accumulates the file sizes of fileStructure and adds that info to the elements with isDirectory = true.
 * @param fileStructure The file structure to accumulate sizes for.
 * @returns The accumulated file sizes.
 * @author @CorradoSurmanowicz
 */
export const accumulateFileSizes = (fileStructure: FilesStructure | FileStructureVersion) => {
  const accumulateSizes = (structure: FilesStructure | FileStructureVersion): number => {
    return Object.entries(structure).reduce((totalSize, [, value]) => {
      if (value.isDirectory && value.contents) {
        value.size = accumulateSizes(value.contents);
      }
      return totalSize + (value.size || 0);
    }, 0);
  };
  accumulateSizes(fileStructure);
};

export const buildTree = <T extends TreeElementBase>(
  files: FilesStructure | FileStructureVersion | TreeElements<T>,
  fullpath?: string,
  struct: TreeElements<T> = []
) => {
  Object.entries(files).forEach(([name, content]) => {
    let this_obj = content as TreeElement<T>;
    if (fullpath) {
      if (this_obj.path) {
        this_obj = { ...this_obj, path: [this_obj.path, fullpath, name].join("/"), id: name, parentId: fullpath };
      } else {
        this_obj = {
          ...this_obj,
          path: [fullpath, name].join("/"),
          id: [fullpath, name].join("/"),
          parentId: fullpath,
        };
      }
    } else {
      this_obj = { ...this_obj, id: name, path: name, parentId: undefined };
    }
    if (this_obj.hasOwnProperty("isDirectory") && this_obj.hasOwnProperty("contents") && this_obj.contents) {
      // console.log("Folder", name, content, fullpath)
      struct.push({
        ...this_obj,
        id: this_obj.id,
        parentId: this_obj.parentId,
        name: this_obj.name,
        hasChildren: [],
      });
      buildTree(this_obj.contents, fullpath ? [fullpath, name].join("/") : name, struct);
    } else {
      // console.log("Child", name, content)
      this_obj = { ...this_obj, name: name };
      struct.push(this_obj);
    }
  });
};

export const collectDeleteActionsUnderPath = (
  filesStructureVersions: FileStructureVersionEntry[] | undefined,
  selectedVersion: number | null,
  folderPath: string
) => {
  const targetVersion =
    selectedVersion !== null
      ? filesStructureVersions?.find((version) => version.datasetVersion === selectedVersion)
      : filesStructureVersions?.[filesStructureVersions.length - 1];

  if (!targetVersion) {
    return [];
  }

  const tree: TreeElements<FilesTreeElement> = [];
  buildTree(targetVersion.structure, undefined, tree);

  const pathsToDelete = tree
    .filter((entry) => (entry.path === folderPath || entry.path.startsWith(`${folderPath}/`)) && !entry.isDirectory)
    .sort((left, right) => right.path.split("/").length - left.path.split("/").length)
    .map((entry) => ({
      id: entry.id,
      path: entry.path,
      action: "delete" as const,
    }));

  return pathsToDelete;
};
interface Props {
  dataset: Dataset;
  fullscreen?: {
    component: string;
    state: boolean;
  };
  fullScreenToggle?: (component: FullScreenOpts["component"]) => void;
}
export const Files = ({ dataset, fullscreen, fullScreenToggle }: Props) => {
  const queryClient = useQueryClient();
  const context = useContext(DatasetContext);
  const { session } = useContext(SessionContext);
  const [showModal, setShowModal] = useState<[id: string, version: number | null]>();
  const dirInputRef = createRef<HTMLInputElement>();
  const fileInputRef = createRef<HTMLInputElement>();
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const { analyzeFiles } = useMatch({
    parserWhiteList: [dataset.format?.id || ""],
    parserBlackList: defaultDatasetParsersBlackList,
  });

  const { readFileList, setLoadingStatus } = useFileHandlingUtils({
    parserBlackList: defaultDatasetParsersBlackList,
    parserWhiteList: [dataset.format?.id || ""],
    setLoading: setLoading,
  });
  const { generateDatasetSubmissionMultiForm } = useFileUploadUtils();

  const { updateDatasetMutation } = useDatasetUploadUtils(dataset.id);
  const { mutateAsync: updateDatasetMutationAsync, isLoading: isLoadingUpdateDatasetMutation } = updateDatasetMutation;

  const onModalCloseCallback = useCallback(() => {
    setShowModal(undefined);
  }, []);

  const onFileClick = useCallback((item: TreeElementBase) => {
    setShowModal([item.id, item.fileVersion || null]);
  }, []);

  const fileControls = useCallback(
    (item: FilesTreeElement) => (
      <DatasetFilesControls
        datasetId={dataset.id}
        item={item}
        defaultShowModal={showModal?.[0] === item.id && showModal?.[1] === item.fileVersion}
        onModalCloseCallback={onModalCloseCallback}
        permissions={dataset.permissions}
      />
    ),
    [dataset.id, dataset.permissions, onModalCloseCallback, showModal]
  );

  const handleUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const target = e.target;
      if (target.files) {
        setLoading(true);
        const files = await readFileList(target.files);
        const matchResult = await analyzeFiles(files, setLoadingStatus).catch((err) => {
          showtoast("error", `Error: ${err && err.hasOwnProperty("message") ? err.message : err}`);
          setLoading(false);
          return undefined;
        });
        if (matchResult && dataset.format && Object.hasOwn(matchResult, dataset.format.id)) {
          const _dataset = matchResult[dataset.format.id].datasets;
          if (!Array.isArray(_dataset) || _dataset.length === 0) {
            showtoast(
              "error",
              `Error: Uploaded files match format ${dataset.format.id} but no dataset was identified. Please check the file paths and try again.`
            );
          } else if (Array.isArray(_dataset) && _dataset.length > 1) {
            showtoast(
              "error",
              `Error: Uploaded files match format ${dataset.format.id} but multiple datasets were identified. Please check the file paths and try again.`
            );
          } else {
            const current_dataset = _dataset[0];
            const datasetForUpload: DatasetForUploadWriteModel = {
              name: dataset.name,
              format: { id: dataset.format.id, name: dataset.format.name },
              files: current_dataset.files.map((d) => ({
                id: d.id,
                isUnique: d.isUnique,
                fullPath: d.fullPath,
                mtime: new Date(d.file.lastModified).toISOString(),
              })),
            };
            const body = generateDatasetSubmissionMultiForm(current_dataset, datasetForUpload);
            await updateDatasetMutationAsync(
              { body, showError: true, onProgress: (progress) => setProgress(progress) },
              {
                onSuccess: () => {
                  showtoast("success", "Files uploaded successfully");
                  queryClient.invalidateQueries([`${datasetsConstants.resource}/${dataset.id}/files/versions`]);
                  queryClient.invalidateQueries([datasetsConstants.resource, "detail", dataset.id]);
                },
              }
            ).catch(() => {});
          }
        } else {
          showtoast(
            "error",
            "Error: Uploaded files do not match the expected format. Please check the file paths and try again."
          );
        }
        setLoading(false);
        target.value = "";
      }
    },
    [
      analyzeFiles,
      dataset.format,
      dataset.id,
      dataset.name,
      generateDatasetSubmissionMultiForm,
      queryClient,
      readFileList,
      setLoadingStatus,
      updateDatasetMutationAsync,
    ]
  );

  return (
    <Container
      title="Files"
      controls={
        <div className="flex row-nowrap align-center gap-5">
          {!!dataset.permissions?.canEdit && !dataset.isViewableEntity && (
            <>
              <FloatingDiv
                triggerComponent={({ ref, getReferenceProps }) => (
                  <Button
                    type="button"
                    className="btn btn-sm btn-soft-primary dropdown-toggle dropdown-toggle-split"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    aria-expanded={dropdownOpen}
                    disabled={loading || isLoadingUpdateDatasetMutation}
                    loading={loading || isLoadingUpdateDatasetMutation}
                    progress={progress}
                    ref={ref}
                    {...getReferenceProps()}
                  >
                    <LucideIcon name="upload" /> Upload
                  </Button>
                )}
              >
                {({ setOpen }) => (
                  <div
                    className="flex col-nowrap"
                    style={{
                      height: "fit-content",
                      background: "var(--white)",
                      border: "1px solid #e0e0e0",
                      borderRadius: 4,
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost-primary"
                      onClick={(e) => {
                        e.preventDefault();
                        fileInputRef.current?.click();
                        setOpen(false);
                      }}
                      style={{ width: "100%", marginRight: "auto", justifyContent: "flex-start" }}
                    >
                      <LucideIcon name="file" /> Upload file
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost-primary"
                      onClick={(e) => {
                        e.preventDefault();
                        dirInputRef.current?.click();
                        setOpen(false);
                      }}
                      style={{ width: "100%", marginRight: "auto", justifyContent: "flex-start" }}
                    >
                      <LucideIcon name="folder" /> Upload folder
                    </button>
                  </div>
                )}
              </FloatingDiv>
              <input
                id="selectFiles"
                ref={fileInputRef}
                hidden
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={handleUpload}
              />
              <input
                id="selectDir"
                ref={dirInputRef}
                hidden
                type="file"
                multiple
                /* @ts-expect-error */
                directory=""
                webkitdirectory=""
                style={{ display: "none" }}
                onChange={handleUpload}
              />
            </>
          )}
          {fullscreen ? (
            <div className={styles.container_controls}>
              <div
                className={styles.container_controls_fullscreen}
                onClick={() => fullScreenToggle && fullScreenToggle("files")}
                title={fullscreen.state ? "Minimize" : "Maximize"}
              >
                {fullscreen?.state ? <LucideIcon name="minimize" /> : <LucideIcon name="maximize" />}
              </div>
            </div>
          ) : undefined}
        </div>
      }
    >
      <div className={styles.container}>
        <div className={styles.container_content}>
          <FilesCore
            dataset={dataset}
            onFileClick={onFileClick}
            fileControls={
              context?.viewShared
                ? session?.permissions.view_unclaimed_datasets
                  ? fileControls
                  : undefined
                : fileControls
            }
          />
        </div>
      </div>
    </Container>
  );
};

interface FilesCoreProps {
  dataset: Dataset;
  onFileClick?: (item: FilesTreeElement) => void;
  fileControls?: (item: FilesTreeElement) => JSX.Element;
}
export const FilesCore = ({ dataset, onFileClick, fileControls }: FilesCoreProps) => {
  const context = useContext(DatasetContext);
  const history = useHistory();
  const { api, session, route } = useContext(SessionContext);
  const [structs, setStructs] = useState<Record<string, TreeElements<FilesTreeElement>>>({});
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const params = new URLSearchParams(window.location.search);
  const datasetVersion = params.get("datasetVersion");

  // Fetch the file structure
  // const { data: filesStructure, status: filesStructureStatus, fetchStatus } = useFileStructure(dataset.id);
  const {
    data: filesStructureVersions,
    status: filesStructureVersionsStatus,
    fetchStatus,
    error,
    isFetched,
  } = useFileStructureVersions(dataset.id);

  useEffect(() => {
    // Pre-process
    if (
      filesStructureVersions &&
      Array.isArray(filesStructureVersions) &&
      filesStructureVersions.length > 0 &&
      isFetched
    ) {
      setLoading(true);
      let _structs = {} as Record<string, TreeElements<FilesTreeElement>>;
      filesStructureVersions.forEach((version) => {
        let struct: TreeElements<FilesTreeElement> = [];
        accumulateFileSizes(version.structure);
        buildTree(version.structure, undefined, struct);
        _structs[version.datasetVersion] = struct;
      });
      if (datasetVersion && filesStructureVersions.some((v) => v.datasetVersion === +datasetVersion)) {
        setSelectedVersion(+datasetVersion);
      } else {
        setSelectedVersion(filesStructureVersions[filesStructureVersions.length - 1].datasetVersion);
      }
      setStructs(_structs);
      setLoading(false);
    } else {
      setLoading(false);
    }
    return () => {
      setLoading(true);
      setStructs({});
      setSelectedVersion(null);
    };
  }, [datasetVersion, filesStructureVersions, isFetched]);

  const { control, watch } = useForm<{ datasetVersion: number | null }>({
    values: { datasetVersion: selectedVersion },
  });

  const _version = watch("datasetVersion");

  useEffect(() => {
    if (_version !== null) setSelectedVersion((prev) => (prev !== _version ? _version : prev));
  }, [_version]);

  const currentVersion = filesStructureVersions?.[filesStructureVersions.length - 1]?.datasetVersion;
  const timeStamps = Object.fromEntries(filesStructureVersions?.map((v) => [v.datasetVersion, v.timestamp]) || []);

  // const handleDeleteFolder = useCallback(async () => {
  //   if (!folderToDelete) return;

  //   setLoading(true);
  //   const filesToDelete = collectDeleteActionsUnderPath(
  //     filesStructureVersions as FileStructureVersionEntry[] | undefined,
  //     selectedVersion,
  //     folderToDelete.path
  //   );
  //   const body = new FormData();
  //   body.append(
  //     "fileActions",
  //     JSON.stringify({
  //       files: filesToDelete,
  //     } as FilesEditInputSchema["fileActions"])
  //   );
  //   await mutateFileEditAsync(
  //     { body, showError: true, onProgress: (progress) => {} },
  //     {
  //       onSuccess: (data) => {
  //         showtoast("success", `Folder ${folderToDelete.path} deleted successfully`);
  //         queryClient.invalidateQueries([`${datasetsConstants.resource}/${dataset.id}/files/versions`]);
  //         queryClient.invalidateQueries([datasetsConstants.resource, "detail", dataset.id]);
  //         setShowDeleteFolderModal(false);
  //         setFolderToDelete(null);
  //       },
  //     }
  //   ).catch((err) => {
  //     setShowDeleteFolderModal(false);
  //     setFolderToDelete(null);
  //   });
  //   setLoading(false);
  // }, [dataset.id, filesStructureVersions, folderToDelete, mutateFileEditAsync, queryClient, selectedVersion]);

  // const folderControls = useCallback(
  //   (item: FilesTreeElement) => {
  //     const path = item.path;
  //     const handleAddToFolder = async (files: FileList, folderPath: string) => {
  //       setLoading(true);
  //       const readFiles = await readFileList(files);
  //       const body = new FormData();
  //       readFiles.forEach((f) => body.append("files", f.file, f.id));
  //       body.append(
  //         "fileActions",
  //         JSON.stringify({
  //           files: readFiles.map((f) => ({
  //             id: f.id,
  //             path: `${folderPath}/${f.fullPath}`,
  //             action: "add",
  //           })),
  //         } as FilesEditInputSchema["fileActions"])
  //       );
  //       await mutateFileEditAsync(
  //         { body, showError: true, onProgress: (progress) => {} },
  //         {
  //           onSuccess: (data) => {
  //             showtoast("success", "Files uploaded successfully");
  //             queryClient.invalidateQueries([`${datasetsConstants.resource}/${dataset.id}/files/versions`]);
  //             queryClient.invalidateQueries([datasetsConstants.resource, "detail", dataset.id]);
  //           },
  //         }
  //       ).catch((err) => {});
  //       setLoading(false);
  //     };

  //     return (
  //       <>
  //         <FloatingDiv
  //           triggerComponent={({ ref, getReferenceProps }) => (
  //             <ControlBtn
  //               icon={<LucideIcon name="plus" />}
  //               disabled={isEditFile || !dataset.permissions?.canEdit}
  //               title={"Add file / folder to " + path}
  //               ref={ref}
  //               {...getReferenceProps()}
  //             />
  //           )}
  //         >
  //           {({ setOpen }) => (
  //             <div
  //               className="flex col-nowrap"
  //               style={{
  //                 height: "fit-content",
  //                 background: "var(--white)",
  //                 border: "1px solid #e0e0e0",
  //                 borderRadius: 4,
  //               }}
  //             >
  //               <button
  //                 type="button"
  //                 className="btn btn-sm btn-ghost-primary"
  //                 onClick={(e) => {
  //                   e.preventDefault();
  //                   const input = document.createElement("input");
  //                   input.type = "file";
  //                   input.multiple = true;
  //                   input.onchange = async (e) => {
  //                     const target = e.target as HTMLInputElement;
  //                     if (target.files && target.files.length > 0) {
  //                       await handleAddToFolder(target.files, path);
  //                     }
  //                     input.remove();
  //                   };
  //                   input.click();
  //                   setOpen(false);
  //                 }}
  //                 style={{ width: "100%", marginRight: "auto", justifyContent: "flex-start" }}
  //               >
  //                 <LucideIcon name="file" /> Upload file
  //               </button>
  //               <button
  //                 type="button"
  //                 className="btn btn-sm btn-ghost-primary"
  //                 onClick={(e) => {
  //                   e.preventDefault();
  //                   const input = document.createElement("input");
  //                   input.type = "file";
  //                   input.multiple = true;
  //                   input.setAttribute("webkitdirectory", "");
  //                   input.setAttribute("directory", "");
  //                   input.onchange = async (e) => {
  //                     const target = e.target as HTMLInputElement;
  //                     if (target.files && target.files.length > 0) {
  //                       await handleAddToFolder(target.files, path);
  //                     }
  //                     input.remove();
  //                   };
  //                   input.click();
  //                   setOpen(false);
  //                 }}
  //                 style={{ width: "100%", marginRight: "auto", justifyContent: "flex-start" }}
  //               >
  //                 <LucideIcon name="folder" /> Upload folder
  //               </button>
  //             </div>
  //           )}
  //         </FloatingDiv>
  //         <ControlBtn
  //           icon={isEditFile ? <LucideIcon name="loader" /> : <LucideIcon name="trash-2" />}
  //           onClick={() => {
  //             setFolderToDelete(item);
  //             setShowDeleteFolderModal(true);
  //           }}
  //           disabled={isEditFile || !dataset.permissions?.canEdit}
  //           title={"Remove folder " + path}
  //         />
  //       </>
  //     );
  //   },
  //   [dataset.permissions?.canEdit, isEditFile, mutateFileEditAsync, queryClient, dataset.id, readFileList]
  // );

  return (
    <LoadingWrapper status={filesStructureVersionsStatus} fetchStatus={fetchStatus} error={error}>
      {loading ? (
        <Skeleton type="rows" />
      ) : Array.isArray(filesStructureVersions) && !!filesStructureVersions.length ? (
        <>
          {!dataset.isViewableEntity && (
            <div className="flex row-nowrap align-center gap-5" style={{ padding: 5 }}>
              <span style={{ fontWeight: "bold", marginRight: "10px" }}>Version:</span>
              <GenericVirtualizedSelectFormField
                id="datasetVersion"
                control={control}
                items={[...filesStructureVersions].reverse().map((version) => version.datasetVersion)}
                onRowEndRenderer={(item) => (
                  <>
                    {item === currentVersion && (
                      <label className="label label-soft-success" style={{ margin: 0 }}>
                        Current
                      </label>
                    )}
                    <div style={{ marginLeft: "auto" }}>
                      <DateTimeRenderer date={timeStamps[item]} style={{ marginLeft: "auto" }} includeElapsed={false} />
                    </div>
                  </>
                )}
                horizontal
                uncontained
              />
              {selectedVersion !== null ? (
                context?.viewShared ? (
                  session?.permissions.view_unclaimed_datasets ? (
                    <div className="flex row-nowrap align-center gap-5">
                      <DownloadDatasetVersion api={api} id={dataset.id} version={selectedVersion} />
                      {context?.mWebEnabled && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => history.push(`${history.location.pathname}?datasetVersion=${selectedVersion}`)}
                        >
                          <LucideIcon name="chart-scatter" /> Show version: {selectedVersion} in MWeb
                        </button>
                      )}
                    </div>
                  ) : undefined
                ) : (
                  <div className="flex row-nowrap align-center gap-5">
                    <DownloadDatasetVersion api={api} id={dataset.id} version={selectedVersion} />
                    {context?.mWebEnabled && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() =>
                          history.replace(
                            `${route(
                              getDetailLink(datasetsConstants.frontendIndexRoute, dataset.id)
                            )}?datasetVersion=${selectedVersion}`
                          )
                        }
                      >
                        <LucideIcon name="chart-scatter" /> Show version: {selectedVersion} in MWeb
                      </button>
                    )}
                  </div>
                )
              ) : (
                <></>
              )}
            </div>
          )}
          {/* <AlertModal
            type={"danger"}
            showModal={showDeleteFolderModal}
            setShowModal={setShowDeleteFolderModal}
            title={"Confirm permanent folder deletion"}
            description={`Are you sure you want to permanently delete all files in folder "${folderToDelete?.path || ""}"? This action cannot be undone.`}
            proceedLabel={"Delete"}
            onProceed={handleDeleteFolder}
            loading={isEditFile}
          /> */}
          {structs && selectedVersion !== null && (
            <FileTree<FilesTreeElement>
              files={getTreeData<FilesTreeElement>(structs[selectedVersion])}
              level={0}
              onFileClick={onFileClick}
              // folderControls={
              //   context?.viewShared
              //     ? session?.permissions.view_unclaimed_datasets
              //       ? folderControls
              //       : undefined
              //     : folderControls
              // }
              fileControls={fileControls}
              onRowRenderer={(item) => (
                <>
                  {item.state && (
                    <label
                      className={`label label-soft-${item.state === "MODIFIED" ? "warning" : "success"}`}
                      style={{ margin: 0 }}
                    >
                      {item.state}
                    </label>
                  )}
                </>
              )}
            />
          )}
        </>
      ) : (
        <Alert type="warning" message="No dataset files" fit>
          We couldn't find any files for this dataset. This could be because all files were deliberately removed, or
          there was an error fetching the file structure. Please try again later or contact support if the issue
          persists.
        </Alert>
      )}
    </LoadingWrapper>
  );
};
